"use client";

// =========================================================================
// TimelineItemMeta — the single, quiet footer line for a timeline row. Replaces
// the two old tinted panels (CommunicationMetadata + TicketUpdateMetadata) that
// each re-rendered a MessageSquare icon and leaked the internal `connectionId`.
// Now: one muted, wrap-friendly row of only the signal that adds information —
// amount / due / priority / attachments / "via <provider>" / ticket author.
// =========================================================================

import { Calendar, DollarSign, Download, File, Paperclip, User } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { format } from "date-fns";
import type { TimelineItem } from "../hooks/timeline-types";
import { formatCurrency } from "@west-tennessee-consulting/ui/lib/currency";

interface TimelineItemMetaProps {
  item: TimelineItem;
}

function providerLabel(provider?: string): string | null {
  if (!provider) return null;
  return provider.charAt(0).toUpperCase() + provider.slice(1);
}

export function TimelineItemMeta({ item }: TimelineItemMetaProps) {
  const via = providerLabel(item.metadata?.provider ?? item.provider);
  const attachments = item.attachments ?? [];
  // The count is the claim; the manifest is the detail. A row can know it has
  // three files without carrying their names (older producer, or a manifest that
  // failed validation) — say "3 attachments" rather than nothing.
  const attachmentCount = Math.max(item.attachment_count ?? 0, attachments.length);
  const amount =
    typeof item.amount === "number"
      ? formatCurrency(item.amount, { currency: item.currency || "USD", fallback: "", minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : null;

  const ticketAuthor =
    item.type === "ticket_update"
      ? item.metadata?.user_name || item.metadata?.user_email || null
      : null;

  const facts: React.ReactNode[] = [];
  if (amount) {
    facts.push(
      <span key="amount" className="inline-flex items-center gap-1">
        <DollarSign className="h-3 w-3" />
        {amount}
      </span>,
    );
  }
  if (item.due_date) {
    facts.push(
      <span key="due" className="inline-flex items-center gap-1">
        <Calendar className="h-3 w-3" />
        Due {format(new Date(item.due_date), "MMM d, yyyy")}
      </span>,
    );
  }
  if (via) {
    facts.push(<span key="via">via {via}</span>);
  }
  if (ticketAuthor) {
    facts.push(
      <span key="author" className="inline-flex items-center gap-1">
        <User className="h-3 w-3" />
        {ticketAuthor}
      </span>,
    );
  }
  if (item.file_name) {
    facts.push(
      <span key="file" className="inline-flex items-center gap-1 min-w-0">
        <File className="h-3 w-3 flex-shrink-0" />
        <span className="truncate max-w-[12rem]">{item.file_name}</span>
      </span>,
    );
  }

  const hasPriority = Boolean(item.priority);
  if (facts.length === 0 && attachmentCount === 0 && !hasPriority) return null;

  return (
    <div className="mt-2 space-y-1.5 text-xs text-muted-foreground">
      {(facts.length > 0 || hasPriority) && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          {facts}
          {hasPriority && (
            <Badge variant="outline" className="text-[10px] font-normal capitalize">
              {item.priority} priority
            </Badge>
          )}
        </div>
      )}

      {attachmentCount > 0 && (
        <div className="flex flex-col gap-1">
          <span className="inline-flex items-center gap-1 font-medium">
            <Paperclip className="h-3 w-3" />
            {attachmentCount} attachment{attachmentCount === 1 ? "" : "s"}
          </span>
          {attachments.slice(0, 3).map((attachment, index) => (
            <div
              key={index}
              className="flex items-center justify-between gap-2 rounded bg-muted/50 px-2 py-1"
            >
              <span className="inline-flex min-w-0 items-center gap-1">
                <File className="h-3 w-3 flex-shrink-0" />
                <span className="truncate">{attachment.name}</span>
                {typeof attachment.size === "number" && (
                  <span className="flex-shrink-0 opacity-70">
                    ({(attachment.size / 1024).toFixed(1)}KB)
                  </span>
                )}
              </span>
              {attachment.url && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  asChild
                  className="h-5 w-5 flex-shrink-0 p-0"
                >
                  <a
                    href={attachment.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Download className="h-3 w-3" />
                  </a>
                </Button>
              )}
            </div>
          ))}
          {attachments.length > 3 && (
            <span className="opacity-70">… and {attachments.length - 3} more</span>
          )}
        </div>
      )}
    </div>
  );
}
